import { createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from "react";
import type { ReactNode } from "react";
import { initialGameState, reduce } from "../domain/reducer/reducer.js";
import type { Command, GameState } from "../domain/types/index.js";
import {
  clearSave,
  hasSave,
  loadGame,
  saveGame,
  shouldPersist,
} from "../infrastructure/saveRepository.js";
import { readBootState } from "./readBootState.js";
import { playTransitionAudio } from "../audio/playTransitionAudio.js";
import { bgmForState, syncGameBgm } from "../audio/bgm/gameBgm.js";
import { useGameBgm } from "../audio/bgm/useGameBgm.js";
import { recordNodeEvent, recordRunFinished, recordRunStarted } from "../infrastructure/playLogApi.js";

type RestoreAction = { type: "RESTORE"; state: GameState };
type ProviderAction = Command | RestoreAction;

type GameContextValue = {
  state: GameState;
  dispatch: (command: Command) => void;
  hasSave: boolean;
  loadError: string | null;
};

const GameContext = createContext<GameContextValue | null>(null);

function providerReducer(state: GameState, action: ProviderAction): GameState {
  if (action.type === "RESTORE") return action.state;
  return reduce(state, action);
}

function isOverlayOnly(prev: GameState, next: GameState): boolean {
  return (
    prev.run === next.run &&
    prev.screen === next.screen &&
    prev.resolution === next.resolution &&
    prev.lastRejection === next.lastRejection &&
    prev.overlay !== next.overlay
  );
}

export function GameProvider({
  children,
  skipSave = false,
  start,
}: {
  children: ReactNode;
  skipSave?: boolean;
  start?: Command | readonly Command[];
}) {
  const startCommands: readonly Command[] = start
    ? Array.isArray(start) ? start : [start as Command]
    : [];
  const boot = useRef(
    skipSave
      ? {
          state: startCommands.reduce((state, command) => reduce(state, command), initialGameState()),
          loadError: null,
        }
      : readBootState(),
  );
  const [state, rawDispatch] = useReducer(providerReducer, boot.current.state);
  const [savedExists, setSavedExists] = useState(hasSave());
  const prevRef = useRef(state);
  const stateRef = useRef(state);
  const completedRunIdsRef = useRef(new Set<string>());
  stateRef.current = state;

  useGameBgm(state);

  const dispatch = useCallback((command: Command) => {
    if (command.type === "RESUME") {
      const loaded = loadGame();
      if (loaded) {
        void syncGameBgm(bgmForState(loaded));
        rawDispatch({ type: "RESTORE", state: loaded });
      }
      return;
    }
    const dispatchedCommand = command.type === "START_NEW" && !command.runId
      ? { ...command, runId: crypto.randomUUID() }
      : command;
    const previous = stateRef.current;
    const next = reduce(previous, dispatchedCommand);
    if (dispatchedCommand.type === "START_NEW" && next.run && next.run !== previous.run) {
      void recordRunStarted(next.run);
    }
    if (dispatchedCommand.type === "SELECT_NODE" && previous.run && next.run
      && previous.screen === "map" && previous.run.availableNodeIds.includes(dispatchedCommand.nodeId)) {
      void recordNodeEvent(previous.run.runId, "node_entered", dispatchedCommand.nodeId);
    }
    if (previous.run && next.run) {
      const clearedBefore = new Set(previous.run.clearedNodeIds);
      for (const nodeId of next.run.clearedNodeIds) {
        if (!clearedBefore.has(nodeId)) void recordNodeEvent(next.run.runId, "node_cleared", nodeId);
      }
      if (!previous.run.outcome && next.run.outcome === "gameover") {
        const nodeId = next.run.activeCase?.nodeId ?? next.run.currentNodeId;
        void recordNodeEvent(next.run.runId, "node_dropped", nodeId);
      }
    }
    if (dispatchedCommand.type === "ABANDON_RUN" && previous.run && next.run === null) {
      completedRunIdsRef.current.add(previous.run.runId);
      void recordRunFinished(previous.run, "abandoned");
    }
    rawDispatch(dispatchedCommand);
    playTransitionAudio(previous, next, dispatchedCommand);
  }, []);

  useEffect(() => {
    const prev = prevRef.current;
    if (isOverlayOnly(prev, state)) {
      prevRef.current = state;
      return;
    }
    if (skipSave) {
      prevRef.current = state;
      return;
    }
    if (shouldPersist(state)) saveGame(state);
    else if (state.screen === "title" && !state.run) clearSave();
    setSavedExists(hasSave());
    prevRef.current = state;
  }, [state, skipSave]);

  useEffect(() => {
    const run = state.run;
    if (!run || !run.outcome || completedRunIdsRef.current.has(run.runId)) return;
    completedRunIdsRef.current.add(run.runId);
    void recordRunFinished(run, run.outcome);
  }, [state.run]);

  useEffect(() => {
    const onHide = () => {
      if (!skipSave && shouldPersist(state)) saveGame(state);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") onHide();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onHide);
    };
  }, [state, skipSave]);

  const value: GameContextValue = {
    state,
    dispatch,
    hasSave: savedExists,
    loadError: boot.current.loadError,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
}
